"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { format, parseISO, parse } from "date-fns";
import TagChips from "./Pieceworktags";

const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

// ── Rate source badge ─────────────────────────────────────────────────────
function RateSourceBadge({ source }) {
  if (!source || source === "none") return null;
  const map = {
    db: {
      label: "DB",
      classes: "bg-blue-900/50 text-blue-300 border-blue-700/50",
      title: "Rate from Item Database catalog",
    },
    budget: {
      label: "Budget",
      classes: "bg-amber-900/40 text-amber-300 border-amber-700/40",
      title: "Rate from budget row (no catalog entry)",
    },
    manual: {
      label: "Manual",
      classes: "bg-purple-900/50 text-purple-300 border-purple-700/50",
      title: "Rate manually overridden",
    },
  };
  const cfg = map[source];
  if (!cfg) return null;
  return (
    <span
      title={cfg.title}
      className={cls(
        "px-1.5 py-0.5 text-[9px] font-bold rounded border tracking-wide",
        cfg.classes,
      )}
    >
      {cfg.label}
    </span>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────
function QtyBar({ installed, pending, estimated }) {
  const target = estimated > 0 ? estimated : installed + pending;
  const instPct = target > 0 ? Math.min(100, (installed / target) * 100) : 0;
  const pendPct =
    target > 0 ? Math.min(100, ((installed + pending) / target) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="relative flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-yellow-600/40 rounded-full"
          style={{ width: `${pendPct}%` }}
        />
        <div
          className={cls(
            "absolute left-0 top-0 h-full rounded-full transition-all",
            instPct >= 100 ? "bg-green-500" : "bg-orange-500",
          )}
          style={{ width: `${instPct}%` }}
        />
      </div>
      <span className="text-[10px] text-gray-400 shrink-0 font-mono whitespace-nowrap">
        {fmtQty(installed)}/{fmtQty(target)}
      </span>
    </div>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────
function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  confirmColor = "orange",
  onConfirm,
  onClose,
}) {
  if (!open) return null;
  const colorMap = {
    orange: "bg-orange-500 hover:bg-orange-600",
    green: "bg-green-600 hover:bg-green-700",
    red: "bg-red-600 hover:bg-red-700",
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        <h3 className="text-base font-semibold text-orange-400 mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-300 mb-5">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={cls(
              "px-4 py-2 text-sm text-white rounded-lg font-semibold",
              colorMap[confirmColor],
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PieceworkerLogDetail3() {
  const { id: logId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // ── Core state ────────────────────────────────────────────────────────────
  const [log, setLog] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [projectWOs, setProjectWOs] = useState([]); // piecework WOs for this project
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [confirm, setConfirm] = useState(null);

  // Notes
  const [editNotes, setEditNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Edit state
  const [editWorkerId, setEditWorkerId] = useState("");
  const [editWorkOrderId, setEditWorkOrderId] = useState(""); // NEW: allow WO change
  const [editWorkDate, setEditWorkDate] = useState("");
  // Per line item: { [budgetItemId]: { qty: string, unitRate: string, rateSource: string } }
  const [editLineState, setEditLineState] = useState({});
  const [removedIds, setRemovedIds] = useState(new Set());

  // Add-items panel
  const [availItems, setAvailItems] = useState([]);
  const [addQtys, setAddQtys] = useState({});
  // addLineRates: { [itemId]: { unitRate: string, rateSource: string } }
  const [addLineRates, setAddLineRates] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // WO budget groups for availability panel
  const [woBudgetGroups, setWoBudgetGroups] = useState([]);

  // Filters
  const [bidAreaFilter, setBidAreaFilter] = useState("all");

  const itemKey = (li, idx) => {
    if (!li) return `__idx_${idx}`;
    return li.budgetItemId != null
      ? li.budgetItemId.toString()
      : `__idx_${idx}`;
  };
  // ── Helpers ───────────────────────────────────────────────────────────────
  const getLineEditState = (li, idx) => {
    if (!li) return {};
    return editLineState[itemKey(li, idx)] || {};
  };

  const setLineField = (li, idx, field, value) => {
    if (!li) return;
    setEditLineState((prev) => {
      const k = itemKey(li, idx);
      return { ...prev, [k]: { ...prev[k], [field]: value } };
    });
  };

  // ── Fetch WO budget groups ─────────────────────────────────────────────
  const fetchWoBudgetGroups = useCallback(async (woId) => {
    if (!woId) return;
    try {
      const res = await axios.get(
        `${API}/pieceworkerLogs3/work-order/${woId}/available-items`,
        { headers },
      );
      setWoBudgetGroups(res.data.budgetGroups || []);
    } catch {
      // non-fatal
    }
  }, []);

  // ── Fetch project work orders (for WO change selector) ────────────────
  const fetchProjectWOs = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/workorders3/project/${projectId}`, {
        headers,
      });
      const wos = (res.data.workOrders || []).filter((w) =>
        ["piecework", "union_piecework"].includes(w.type),
      );
      setProjectWOs(wos);
    } catch {
      // non-fatal
    }
  }, []);

  // ── Load log ──────────────────────────────────────────────────────────────
  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pieceworkerLogs3/${logId}`, {
        headers,
      });
      const logData = res.data.data;
      setLog(logData);
      setEditWorkerId(logData.worker?._id || logData.worker || "");
      setEditWorkOrderId(logData.workOrder?._id || logData.workOrder || "");
      setEditNotes(logData.notes || "");
      setEditWorkDate(
        logData.workCompletedDate
          ? logData.workCompletedDate.split("T")[0]
          : "",
      );
      const lineState = {};
      (logData.lineItems || []).filter(Boolean).forEach((li, idx) => {
        lineState[itemKey(li, idx)] = {
          qty: String(li.quantity || 0),
          unitRate: String(li.unitRate || 0),
          rateSource: li.rateSource || "budget",
        };
      });
      setEditLineState(lineState);
      setRemovedIds(new Set());
      const woId = logData.workOrder?._id || logData.workOrder;
      const projectId = logData.project?._id || logData.project;
      fetchWoBudgetGroups(woId);
      fetchProjectWOs(projectId);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [logId, fetchWoBudgetGroups, fetchProjectWOs]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  // Load pieceworker list when in edit mode
  useEffect(() => {
    if (!log || log.status !== "pending") return;
    axios
      .get(`${API}/users/pieceworkers`, { headers })
      .then((r) => setWorkers(r.data || []))
      .catch(() => {});
  }, [log?.status]);

  // When edit WO changes, re-fetch available items for that WO
  useEffect(() => {
    if (!editMode || !editWorkOrderId) return;
    fetchWoBudgetGroups(editWorkOrderId);
  }, [editWorkOrderId, editMode]);

  // ── Enter edit mode ───────────────────────────────────────────────────────
  const enterEditMode = async () => {
    setEditMode(true);
    setShowAdd(false);
    if (!log) return;
    const woId = log.workOrder?._id || log.workOrder;
    if (!woId) return;
    setAddLoading(true);
    try {
      const res = await axios.get(
        `${API}/pieceworkerLogs3/work-order/${woId}/available-items`,
        { headers },
      );
      const budgetGroups = res.data.budgetGroups || [];
      setWoBudgetGroups(budgetGroups);

      const existingIds = new Set(
        (log.lineItems || []).map((li) => li.budgetItemId?.toString()),
      );
      const newItems = budgetGroups
        .filter((g) => !existingIds.has(g.primaryParentId?.toString()))
        .map((g) => ({
          _id: g.primaryParentId,
          itemCode: g.itemCode,
          description: g.description,
          sectionNumber: g.sectionNumber,
          bidArea: g.bidArea,
          uom: g.uom,
          unitRate: g.unitRate, // already DB-resolved on the server
          budgetRate: g.budgetRate, // original prorated rate (for display)
          rateSource: g.rateSource, // "db" | "budget"
          laborHours: g.laborHours || 0,
          qtyEstimated: g.qtyEstimated,
          qtyInstalled: g.qtyInstalled,
          qtyPending: g.qtyPending,
          qtyRemaining: g.qtyRemaining,
        }));

      setAvailItems(newItems);
      const initQtys = {};
      const initRates = {};
      newItems.forEach((i) => {
        initQtys[i._id] = "";
        initRates[i._id] = {
          unitRate: String(i.unitRate || 0),
          rateSource: i.rateSource || "db",
        };
      });
      setAddQtys(initQtys);
      setAddLineRates(initRates);
    } catch (e) {
      console.warn("Could not load available items:", e.message);
    } finally {
      setAddLoading(false);
    }
  };

  const exitEditMode = () => {
    setEditMode(false);
    setShowAdd(false);
    setRemovedIds(new Set());
    setEditWorkOrderId(log?.workOrder?._id || log?.workOrder || "");
    setEditWorkDate(
      log?.workCompletedDate ? log.workCompletedDate.split("T")[0] : "",
    );
    const lineState = {};
    (log?.lineItems || []).filter(Boolean).forEach((li, idx) => {
      lineState[itemKey(li, idx)] = {
        qty: String(li.quantity || 0),
        unitRate: String(li.unitRate || 0),
        rateSource: li.rateSource || "budget",
      };
    });
    setEditLineState(lineState);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    setNotesSaved(false);
    try {
      const res = await axios.patch(
        `${API}/pieceworkerLogs3/${logId}/notes`,
        { notes: editNotes },
        { headers },
      );
      setLog((prev) => ({
        ...prev,
        notes: editNotes,
        units: res.data?.data?.units ?? prev.units,
      }));
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSavingNotes(false);
    }
  };
  const handleSave = async () => {
    setActing(true);
    setError("");
    try {
      const existing = (log.lineItems || [])
        .filter(Boolean)
        .filter((li, idx) => !removedIds.has(itemKey(li, idx)))
        .map((li, idx) => {
          const state = getLineEditState(li, idx);
          const qty = parseFloat(state.qty ?? li.quantity) || 0;
          const rawRate = parseFloat(state.unitRate ?? li.unitRate) || 0;
          const originalRate = li.unitRate || 0;
          const rateChanged = Math.abs(rawRate - originalRate) > 0.001;
          const rateSource = rateChanged
            ? "manual"
            : state.rateSource || li.rateSource || "budget";
          return {
            budgetItemId: li.budgetItemId,
            itemCode: li.itemCode,
            description: li.description,
            sectionNumber: li.sectionNumber,
            bidArea: li.bidArea,
            uom: li.uom,
            unitRate: rawRate,
            rateSource,
            quantity: qty,
            laborHours: li.laborHours || 0,
          };
        })
        .filter((li) => li.quantity > 0);

      const added = availItems
        .filter((i) => parseFloat(addQtys[i._id] || "0") > 0)
        .map((i) => {
          const rateState = addLineRates[i._id] || {};
          const rawRate = parseFloat(rateState.unitRate ?? i.unitRate) || 0;
          const originalRate = i.unitRate || 0;
          const rateChanged = Math.abs(rawRate - originalRate) > 0.001;
          const rateSource = rateChanged
            ? "manual"
            : rateState.rateSource || i.rateSource || "db";
          return {
            budgetItemId: i._id,
            itemCode: i.itemCode,
            description: i.description,
            sectionNumber: i.sectionNumber,
            bidArea: i.bidArea,
            uom: i.uom,
            unitRate: rawRate,
            rateSource,
            quantity: parseFloat(addQtys[i._id]),
            laborHours: 0,
          };
        });

      const combined = [...existing, ...added];
      if (combined.length === 0) {
        setError("At least one line item required.");
        setActing(false);
        return;
      }

      await axios.patch(
        `${API}/pieceworkerLogs3/${logId}`,
        {
          lineItems: combined,
          workerId: editWorkerId,
          workOrderId: editWorkOrderId || undefined,
          workCompletedDate: editWorkDate || undefined,
        },
        { headers },
      );
      await fetchLog();
      setEditMode(false);
      setShowAdd(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setActing(false);
    }
  };

  // ── Status actions ────────────────────────────────────────────────────────
  const doAction = async (action) => {
    setActing(true);
    setError("");
    try {
      await axios.post(
        `${API}/pieceworkerLogs3/${logId}/${action}`,
        {},
        { headers },
      );
      const backTab =
        location.state?.activeStatusTab ||
        { approve: "Approved", reject: "Rejected", reactivate: "Pending" }[
          action
        ] ||
        "Pending";

      if (location.state?.fromMainTab) {
        navigate("/pieceworkerlog", { state: { activeTab: backTab } });
        return;
      }

      const projectId = log?.project?._id || log?.project;
      if (projectId && location.state?.fromProject) {
        navigate(`/Projects/${projectId}`, {
          state: { initialTab: "Worksheets", activeStatusTab: backTab },
        });
      } else {
        navigate("/pieceworkerlog", { state: { activeTab: backTab } });
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setActing(false);
      setConfirm(null);
    }
  };

  // ── Back navigation ───────────────────────────────────────────────────────
  const handleBack = () => {
    const statusTab =
      { pending: "Pending", approved: "Approved", rejected: "Rejected" }[
        log?.status
      ] || "Pending";

    if (location.state?.fromMainTab) {
      navigate("/pieceworkerlog", {
        state: { activeTab: location.state?.activeStatusTab || statusTab },
      });
      return;
    }

    const projectId = log?.project?._id || log?.project;
    if (location.state?.fromProject && projectId) {
      navigate(`/Projects/${projectId}`, {
        state: {
          initialTab: "Worksheets",
          activeStatusTab: location.state?.activeStatusTab || statusTab,
        },
      });
    } else {
      navigate("/pieceworkerlog", { state: { activeTab: statusTab } });
    }
  };

  const goToLogs = ({ search = "", tags = [] } = {}) => {
    const projectId = log?.project?._id || log?.project;
    const fromProject = !!location.state?.fromProject && !!projectId;
    const key = fromProject
      ? `pwlogs3_filters_${projectId}`
      : "pwlogs3_main_filters";
    try {
      const prev = JSON.parse(sessionStorage.getItem(key)) || {};
      sessionStorage.setItem(
        key,
        JSON.stringify({
          ...prev,
          search,
          tagFilter: tags,
          statusFilter: "all",
          woFilter: "all",
          dateFrom: "",
          dateTo: "",
          ...(fromProject
            ? {}
            : {
                projectFilter: "all",
                workerFilter: "all",
                workDateFrom: "",
                workDateTo: "",
              }),
          page: 1,
        }),
      );
    } catch {}
    if (fromProject)
      navigate(`/Projects/${projectId}`, {
        state: { initialTab: "Worksheets" },
      });
    else navigate("/pieceworkerlog");
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const uniqueBidAreas = useMemo(() => {
    const s = new Set(
      (log?.lineItems || []).map((i) => i.bidArea).filter(Boolean),
    );
    return ["all", ...[...s].sort()];
  }, [log]);

  const filteredLineItems = useMemo(
    () =>
      (log?.lineItems || []).filter(
        (i) => bidAreaFilter === "all" || i.bidArea === bidAreaFilter,
      ),
    [log, bidAreaFilter],
  );

  // Live cost totals in edit mode
  const totals = useMemo(() => {
    if (!editMode) {
      const items = log?.lineItems || [];
      return {
        subtotal: items.reduce((s, i) => s + (i?.subtotal || 0), 0),
        burden: items.reduce((s, i) => s + (i?.burden || 0), 0),
        total: items.reduce((s, i) => s + (i?.total || 0), 0),
      };
    }
    const existing = (log?.lineItems || [])
      .filter(Boolean)
      .filter((li, idx) => !removedIds.has(itemKey(li, idx)))
      .map((li, idx) => {
        const state = getLineEditState(li, idx);
        const qty = parseFloat(state.qty ?? li.quantity) || 0;
        const rate = parseFloat(state.unitRate ?? li.unitRate) || 0;
        const scale = li.quantity > 0 ? qty / li.quantity : 0;
        const rateScale = li.unitRate > 0 ? rate / li.unitRate : 1;
        return {
          subtotal: (li.subtotal || 0) * scale * rateScale,
          burden: (li.burden || 0) * scale * rateScale,
          total: qty * rate,
        };
      });
    const addedTotals = availItems
      .filter((i) => parseFloat(addQtys[i._id] || "0") > 0)
      .map((i) => {
        const qty = parseFloat(addQtys[i._id]) || 0;
        const rate =
          parseFloat(addLineRates[i._id]?.unitRate ?? i.unitRate) || 0;
        return { subtotal: 0, burden: 0, total: qty * rate };
      });
    const all = [...existing, ...addedTotals];
    return {
      subtotal: all.reduce((s, i) => s + i.subtotal, 0),
      burden: all.reduce((s, i) => s + i.burden, 0),
      total: all.reduce((s, i) => s + i.total, 0),
    };
  }, [
    log,
    editMode,
    editLineState,
    removedIds,
    availItems,
    addQtys,
    addLineRates,
  ]);

  // Back-calculate worker rate from subtotal/qty when workerRate not stored
  const getWorkerRate = (item) => {
    if (item.workerRate != null && item.workerRate > 0) {
      return item.workerRate;
    }
    // Back-calculate: subtotal is pre-burden pay, so workerRate = subtotal / qty
    const qty = item.quantity || 0;
    const subtotal = item.subtotal || 0;
    if (qty > 0 && subtotal > 0) {
      return parseFloat((subtotal / qty).toFixed(3));
    }
    return item.unitRate || 0;
  };

  // ── WO availability rows ──────────────────────────────────────────────────
  // ── WO availability rows ──────────────────────────────────────────────────
  // Matches log line items to WO budget groups using the same composite-key
  // strategy as WorkOrderView3's /items endpoint: (itemCode + unitRate).
  // Falls back to budgetItemId lookup when the log's unitRate was manually
  // overridden (rateSource: "manual") — in which case the composite key
  // won't align with the budget row's key.
  // ── WO availability rows ──────────────────────────────────────────────────
  // Matches log line items to WO budget groups using the same composite-key
  // strategy as the server's /available-items grouping: (itemCode + bidArea + unitRate).
  // Falls back to budgetItemId lookup when the log's unitRate was manually
  // overridden — in which case the composite key won't align with the budget
  // row's key.
  const woAvailRows = useMemo(() => {
    if (!log?.lineItems?.length) return [];

    const KEY = (code, area, rate) =>
      `${(code || "").trim().toLowerCase()}||${(area || "").trim().toLowerCase()}||${
        Math.round((rate || 0) * 100) / 100
      }`;

    const groupByKey = new Map(woBudgetGroups.map((g) => [g.key, g]));
    const groupById = new Map(
      woBudgetGroups.map((g) => [g.primaryParentId?.toString(), g]),
    );

    return (
      (log.lineItems || [])
        // keep each line's ORIGINAL index so it matches the keys used by editLineState
        .map((li, origIdx) => ({ li, origIdx }))
        .filter(
          ({ li }) =>
            li && (bidAreaFilter === "all" || li.bidArea === bidAreaFilter),
        )
        .map(({ li, origIdx }) => {
          const isManual = li.rateSource === "manual";
          const matchRate = isManual
            ? li.workerRate || li.unitRate
            : li.unitRate;

          let group = groupByKey.get(KEY(li.itemCode, li.bidArea, matchRate));
          if (!group && isManual) {
            group = groupByKey.get(KEY(li.itemCode, li.bidArea, li.unitRate));
          }
          if (!group && li.budgetItemId) {
            group = groupById.get(li.budgetItemId.toString());
          }

          const estimated = group?.qtyEstimated ?? li.currentEstimated ?? 0;
          const installed = group?.qtyInstalled ?? li.currentApproved ?? 0;
          const totalPending = group?.qtyPending ?? li.currentPending ?? 0;

          // FIX: pass the line item + its index (not just the budgetItemId), and
          // count a line marked for removal as 0 so the remaining qty is accurate.
          const isRemoved = editMode && removedIds.has(itemKey(li, origIdx));
          const thisLog = editMode
            ? isRemoved
              ? 0
              : parseFloat(getLineEditState(li, origIdx).qty ?? li.quantity) ||
                0
            : li.quantity || 0;

          const pendingOther =
            log.status === "pending"
              ? Math.max(0, totalPending - (li.quantity || 0))
              : Math.max(0, totalPending);
          const remaining =
            log.status === "pending"
              ? Math.max(0, estimated - installed - pendingOther - thisLog)
              : Math.max(0, estimated - installed - pendingOther);

          return {
            budgetItemId: li.budgetItemId,
            itemCode: li.itemCode,
            description: li.description,
            uom: li.uom,
            bidArea: li.bidArea,
            estimated,
            installed,
            pendingOther,
            thisLog,
            remaining,
          };
        })
    );
  }, [log, woBudgetGroups, bidAreaFilter, editMode, editLineState, removedIds]);

  const woTotals = useMemo(
    () => ({
      estimated: woAvailRows.reduce((s, r) => s + r.estimated, 0),
      installed: woAvailRows.reduce((s, r) => s + r.installed, 0),
      pendingOther: woAvailRows.reduce((s, r) => s + r.pendingOther, 0),
      thisLog: woAvailRows.reduce((s, r) => s + r.thisLog, 0),
      remaining: woAvailRows.reduce((s, r) => s + r.remaining, 0),
    }),
    [woAvailRows],
  );

  const addedCount = useMemo(
    () =>
      availItems.filter((i) => parseFloat(addQtys[i._id] || "0") > 0).length,
    [availItems, addQtys],
  );

  // Has the WO been changed in edit mode?
  const woChanged =
    editMode &&
    editWorkOrderId &&
    editWorkOrderId !== (log?.workOrder?._id || log?.workOrder);

  // ── Early returns ─────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-800">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );

  if (error && !log)
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-800">
        <div className="text-red-400 text-lg">{error}</div>
      </div>
    );

  if (!log) return null;

  const isPending = log.status === "pending";
  const isApproved = log.status === "approved";
  const isRejected = log.status === "rejected";
  const statusColor = isApproved
    ? "bg-green-700/60 text-green-200"
    : isRejected
      ? "bg-red-800/60 text-red-200"
      : "bg-yellow-700/60 text-yellow-100";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-full mx-auto px-4 py-6 space-y-5">
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.label}
        confirmColor={confirm?.color}
        onConfirm={() => doAction(confirm?.action)}
        onClose={() => setConfirm(null)}
      />

      {/* ── Header ── */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <button
                onClick={handleBack}
                className="text-sm text-gray-400 hover:text-white"
              >
                ← Back
              </button>
              <span className="text-gray-600">|</span>
              <span className="text-[11px] font-mono text-gray-500">
                {log.logNumber}
              </span>
              <span
                className={cls(
                  "px-2 py-0.5 text-[11px] font-semibold rounded-full",
                  statusColor,
                )}
              >
                {log.status.toUpperCase()}
              </span>
            </div>
            <h2 className="text-lg font-bold text-white">
              Piecework Log — {log.workOrder?.title || "—"}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {log.workOrder?.woNumber}
            </p>
            {(log.moduleName ||
              log.units?.length > 0 ||
              log.tags?.length > 0) && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {log.moduleName && (
                  <span className="px-1.5 py-0.5 text-[10px] rounded border bg-purple-900/30 border-purple-700/40 text-purple-300">
                    {log.moduleName}
                  </span>
                )}
                <TagChips
                  units={log.units}
                  tags={log.tags}
                  max={12}
                  onUnitClick={(u) => goToLogs({ search: `unit ${u}` })}
                  onTagClick={(t) => goToLogs({ tags: [t] })}
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {isPending && !editMode && (
              <button
                onClick={enterEditMode}
                className="px-3 py-1.5 text-xs font-semibold text-orange-300 bg-orange-900/30 border border-orange-700/40 rounded hover:bg-orange-900/50"
              >
                ✎ Edit
              </button>
            )}
            {isPending && !editMode && (
              <>
                <button
                  disabled={acting}
                  onClick={() =>
                    setConfirm({
                      action: "approve",
                      title: "Approve Log",
                      message:
                        "Approve this piecework log? This will mark the quantities as installed.",
                      label: "Approve",
                      color: "green",
                    })
                  }
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-40"
                >
                  ✓ Approve
                </button>
                <button
                  disabled={acting}
                  onClick={() =>
                    setConfirm({
                      action: "reject",
                      title: "Reject Log",
                      message:
                        "Reject this piecework log? The submitted quantities will be released.",
                      label: "Reject",
                      color: "red",
                    })
                  }
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-40"
                >
                  ✕ Reject
                </button>
              </>
            )}
            {isApproved && (
              <button
                disabled={acting}
                onClick={() =>
                  setConfirm({
                    action: "reactivate",
                    title: "Reverse Approval",
                    message:
                      "Move this log back to Pending? The installed quantities will be released.",
                    label: "Reverse Approval",
                    color: "orange",
                  })
                }
                className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-40"
              >
                ↩ Reverse Approval
              </button>
            )}
            {isRejected && (
              <button
                disabled={acting}
                onClick={() =>
                  setConfirm({
                    action: "reactivate",
                    title: "Reactivate Log",
                    message: "Reactivate this log back to pending?",
                    label: "Reactivate",
                    color: "orange",
                  })
                }
                className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-40"
              >
                ↩ Reactivate
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-900/40 border border-red-700 rounded-lg text-sm text-red-300">
          {error}
        </div>
      )}

      {/* ── Meta cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {/* Work Order — editable in edit mode */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
            Work Order
          </p>
          {editMode ? (
            <div>
              <select
                value={editWorkOrderId}
                onChange={async (e) => {
                  setEditWorkOrderId(e.target.value);
                  // Reset available items when WO changes
                  setAvailItems([]);
                  setAddQtys({});
                  setAddLineRates({});
                  setShowAdd(false);
                  // Reload available items for the new WO
                  if (e.target.value) {
                    setAddLoading(true);
                    try {
                      const res = await axios.get(
                        `${API}/pieceworkerLogs3/work-order/${e.target.value}/available-items`,
                        { headers },
                      );
                      const budgetGroups = res.data.budgetGroups || [];
                      setWoBudgetGroups(budgetGroups);
                      const existingIds = new Set(
                        (log.lineItems || []).map((li) =>
                          li.budgetItemId?.toString(),
                        ),
                      );
                      const newItems = budgetGroups
                        .filter(
                          (g) =>
                            !existingIds.has(g.primaryParentId?.toString()),
                        )
                        .map((g) => ({
                          _id: g.primaryParentId,
                          itemCode: g.itemCode,
                          description: g.description,
                          sectionNumber: g.sectionNumber,
                          bidArea: g.bidArea,
                          uom: g.uom,
                          unitRate: g.unitRate,
                          budgetRate: g.budgetRate,
                          rateSource: g.rateSource,
                          laborHours: g.laborHours || 0,
                          qtyEstimated: g.qtyEstimated,
                          qtyInstalled: g.qtyInstalled,
                          qtyPending: g.qtyPending,
                          qtyRemaining: g.qtyRemaining,
                        }));
                      setAvailItems(newItems);
                      const initQtys = {};
                      const initRates = {};
                      newItems.forEach((i) => {
                        initQtys[i._id] = "";
                        initRates[i._id] = {
                          unitRate: String(i.unitRate || 0),
                          rateSource: i.rateSource || "db",
                        };
                      });
                      setAddQtys(initQtys);
                      setAddLineRates(initRates);
                    } catch {
                      // non-fatal
                    } finally {
                      setAddLoading(false);
                    }
                  }
                }}
                className="w-full text-xs bg-gray-700 border border-orange-500/60 rounded px-2 py-1 text-orange-200 focus:border-orange-500 focus:outline-none"
              >
                {projectWOs.map((w) => (
                  <option key={w._id} value={w._id}>
                    {w.woNumber} — {w.title}
                  </option>
                ))}
              </select>
              {woChanged && (
                <p className="text-[10px] text-amber-400 mt-1">
                  ⚠ WO changed — line items will re-link
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-200 font-medium truncate">
              {log.workOrder?.title || "—"}
            </p>
          )}
        </div>
        {/* Worker */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
            Worker
          </p>
          {editMode ? (
            <select
              value={editWorkerId}
              onChange={(e) => setEditWorkerId(e.target.value)}
              className="w-full text-xs bg-gray-700 border border-gray-600 rounded px-2 py-1 text-gray-200 focus:border-orange-500 focus:outline-none"
            >
              {workers.map((w) => (
                <option key={w._id} value={w._id}>
                  {w.displayName || w.email}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-200 font-medium truncate">
              {log.worker?.displayName || log.worker?.email || "—"}
            </p>
          )}
        </div>
        {/* Work Date */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
            Work Date
          </p>
          {editMode ? (
            <input
              type="date"
              value={editWorkDate}
              onChange={(e) => setEditWorkDate(e.target.value)}
              className="w-full text-xs bg-gray-700 border border-orange-500/60 rounded px-2 py-1 text-orange-200 focus:border-orange-500 focus:outline-none"
            />
          ) : (
            <p className="text-sm text-gray-200 font-medium">
              {log.workCompletedDate
                ? format(
                    parse(
                      log.workCompletedDate.split("T")[0],
                      "yyyy-MM-dd",
                      new Date(),
                    ),
                    "MMM d, yyyy",
                  )
                : "—"}
            </p>
          )}
        </div>
        {/* Submitted */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
            Submitted
          </p>
          <p className="text-sm text-gray-200 font-medium">
            {log.submittedAt
              ? format(parseISO(log.submittedAt), "MMM d, yyyy h:mm a")
              : "—"}
          </p>
        </div>
        {/* Union */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
            Union
          </p>
          <p className="text-sm text-gray-200 font-medium">
            {log.workOrder?.union || "Non-union"}
          </p>
        </div>

        {isApproved && (
          <div className="bg-gray-800 border border-green-700/40 rounded-xl p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
              Approved By
            </p>
            <p className="text-sm text-green-300 font-medium">
              {log.approvedBy?.displayName || "—"}
            </p>
            {log.approvedAt && (
              <p className="text-[10px] text-gray-500 mt-0.5">
                {format(parseISO(log.approvedAt), "MMM d, yyyy h:mm a")}
              </p>
            )}
          </div>
        )}
        {isRejected && log.rejectedBy && (
          <div className="bg-gray-800 border border-red-700/40 rounded-xl p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
              Rejected By
            </p>
            <p className="text-sm text-red-300 font-medium">
              {log.rejectedBy?.displayName || "—"}
            </p>
            {log.rejectedAt && (
              <p className="text-[10px] text-gray-500 mt-0.5">
                {format(parseISO(log.rejectedAt), "MMM d, yyyy h:mm a")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Timeline ── */}
      {(log.timeline || []).length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700">
            <h3 className="text-sm font-semibold text-white">
              Activity Timeline
            </h3>
          </div>
          <div className="px-5 py-4">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-3.5 top-0 bottom-0 w-px bg-gray-700" />

              <div className="space-y-4">
                {[...(log.timeline || [])].reverse().map((entry, idx) => {
                  const eventConfig = {
                    created: {
                      dot: "bg-blue-500",
                      label: "Created",
                      labelColor: "text-blue-300",
                      icon: "✦",
                    },
                    edited: {
                      dot: "bg-orange-500",
                      label: "Edited",
                      labelColor: "text-orange-300",
                      icon: "✎",
                    },
                    approved: {
                      dot: "bg-green-500",
                      label: "Approved",
                      labelColor: "text-green-300",
                      icon: "✓",
                    },
                    rejected: {
                      dot: "bg-red-500",
                      label: "Rejected",
                      labelColor: "text-red-300",
                      icon: "✕",
                    },
                    reactivated: {
                      dot: "bg-yellow-500",
                      label: "Reactivated",
                      labelColor: "text-yellow-300",
                      icon: "↩",
                    },
                    note_added: {
                      dot: "bg-gray-500",
                      label: "Note Added",
                      labelColor: "text-gray-300",
                      icon: "📝",
                    },
                  };
                  const cfg = eventConfig[entry.event] || {
                    dot: "bg-gray-500",
                    label: entry.event,
                    labelColor: "text-gray-300",
                    icon: "·",
                  };

                  return (
                    <div
                      key={entry._id || idx}
                      className="relative flex gap-4 pl-8"
                    >
                      {/* Dot */}
                      <div
                        className={cls(
                          "absolute left-0 top-1 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white z-10",
                          cfg.dot,
                        )}
                      >
                        {cfg.icon}
                      </div>

                      {/* Card */}
                      <div className="flex-1 bg-gray-900/60 border border-gray-700/60 rounded-lg px-4 py-3 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap mb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={cls(
                                "text-xs font-semibold",
                                cfg.labelColor,
                              )}
                            >
                              {cfg.label}
                            </span>
                            {entry.actorName && (
                              <span className="text-xs text-gray-400">
                                by{" "}
                                <span className="text-gray-200">
                                  {entry.actorName}
                                </span>
                                {entry.actorRole && (
                                  <span className="text-gray-600 text-[10px] ml-1">
                                    ({entry.actorRole})
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                          {entry.createdAt && (
                            <span className="text-[10px] text-gray-600 shrink-0">
                              {format(
                                parseISO(
                                  typeof entry.createdAt === "string"
                                    ? entry.createdAt
                                    : new Date(entry.createdAt).toISOString(),
                                ),
                                "MMM d, yyyy h:mm a",
                              )}
                            </span>
                          )}
                        </div>

                        {/* Note / rejection reason */}
                        {entry.note && (
                          <p
                            className={cls(
                              "text-xs mt-1 px-2 py-1.5 rounded border",
                              entry.event === "rejected"
                                ? "bg-red-950/40 border-red-800/40 text-red-300"
                                : "bg-gray-800/60 border-gray-700/40 text-gray-400",
                            )}
                          >
                            {entry.note}
                          </p>
                        )}

                        {/* Snapshot summary */}
                        {entry.snapshot?.totalCost != null && (
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <span className="text-[10px] text-gray-600">
                              Total:{" "}
                              <span className="text-orange-300 font-mono">
                                ${fmt(entry.snapshot.totalCost)}
                              </span>
                            </span>
                            {entry.snapshot.lineItemCount != null && (
                              <span className="text-[10px] text-gray-600">
                                {entry.snapshot.lineItemCount} item
                                {entry.snapshot.lineItemCount !== 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Diff lines */}
                        {(entry.snapshot?.changes || []).length > 0 &&
                          entry.snapshot.changes[0] !==
                            "No line item changes detected" && (
                            <ul className="mt-2 space-y-0.5">
                              {entry.snapshot.changes.map((c, i) => (
                                <li
                                  key={i}
                                  className="text-[10px] text-gray-500 font-mono flex items-start gap-1"
                                >
                                  <span className="text-gray-700 shrink-0 mt-px">
                                    →
                                  </span>
                                  {c}
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Line Items ── */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-700 flex flex-wrap gap-3 items-center">
          <h3 className="text-sm font-semibold text-white">Line Items</h3>
          {editMode && (
            <span className="text-[10px] text-gray-500 bg-gray-700/60 rounded px-2 py-0.5">
              Rate sources: <span className="text-blue-300">DB</span> = Item
              Database catalog &nbsp;·&nbsp;
              <span className="text-amber-300">Budget</span> = prorated budget
              rate &nbsp;·&nbsp;
              <span className="text-purple-300">Manual</span> = your override
            </span>
          )}
          {uniqueBidAreas.length > 1 && (
            <select
              value={bidAreaFilter}
              onChange={(e) => setBidAreaFilter(e.target.value)}
              className="ml-auto px-8 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none"
            >
              {uniqueBidAreas.map((a) => (
                <option key={a} value={a}>
                  {a === "all" ? "All Areas" : a}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Notes */}
        <div className="px-5 pt-4 pb-3 border-b border-gray-700/50">
          <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
            <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">
              Notes
            </p>
            <div className="flex items-center gap-2">
              {notesSaved && (
                <span className="text-[11px] text-green-400">✓ Saved</span>
              )}
              {editNotes !== (log.notes || "") && (
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="px-3 py-1 text-xs font-semibold text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-40"
                >
                  {savingNotes ? "Saving…" : "Save Note"}
                </button>
              )}
            </div>
          </div>
          <textarea
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            placeholder="Add a note…"
            rows={2}
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
          />
          <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
            <p className="text-[11px] text-gray-500 italic min-w-0">
              {log.aiSummary ? `✨ ${log.aiSummary}` : "No AI summary yet"}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-900">
              <tr>
                {[
                  ["Code", "text-left"],
                  ["Section", "text-left"],
                  ["Bid Area", "text-left"],
                  ["Description", "text-left min-w-[160px]"],
                  ["UOM", "text-center"],
                  ["Worker Rate", "text-right"],
                  [editMode ? "Unit Rate (edit)" : "Unit Rate", "text-right"],
                  [editMode ? "Qty (edit)" : "Qty Logged", "text-right"],
                  ["Subtotal", "text-right"],
                  ["Burden", "text-right"],
                  ["Total", "text-right"],
                ].map(([h, a]) => (
                  <th
                    key={h}
                    className={cls("px-3 py-2.5 text-gray-400 font-medium", a)}
                  >
                    {h}
                  </th>
                ))}
                {editMode && <th className="w-8" />}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-700/50">
              {filteredLineItems.filter(Boolean).map((item, idx) => {
                const state = getLineEditState(item, idx);
                const editQty = editMode
                  ? parseFloat(state.qty ?? item.quantity) || 0
                  : item.quantity || 0;
                const editRate = editMode
                  ? parseFloat(state.unitRate ?? item.unitRate) || 0
                  : item.unitRate || 0;

                const scale = item.quantity > 0 ? editQty / item.quantity : 0;
                const rateScale =
                  item.unitRate > 0 ? editRate / item.unitRate : 1;
                const dispSub = editMode
                  ? (item.subtotal || 0) * scale * rateScale
                  : item.subtotal || 0;
                const dispBurden = editMode
                  ? (item.burden || 0) * scale * rateScale
                  : item.burden || 0;
                const dispTotal = editMode
                  ? editQty * editRate
                  : item.total || 0;

                const isRemoved = removedIds.has(itemKey(item, idx));
                const currentRateSource = editMode
                  ? state.rateSource || item.rateSource || "budget"
                  : item.rateSource || "budget";

                return (
                  <tr
                    key={itemKey(item, idx)}
                    className={cls(
                      idx % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                      isRemoved && "opacity-40 line-through",
                    )}
                  >
                    <td className="px-3 py-2 font-mono text-orange-400">
                      {item.itemCode || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-400 font-mono text-[11px]">
                      {item.sectionNumber || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-400">
                      {item.bidArea || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-200">
                      {item.description || "—"}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-400">
                      {item.uom || "—"}
                    </td>
                    {/* Worker Rate column */}
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-blue-300 font-mono text-xs">
                          $
                          {parseFloat(
                            getWorkerRate(item).toFixed(3),
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 3,
                            maximumFractionDigits: 3,
                          })}
                        </span>
                        <span className="text-[9px] text-gray-600">
                          pre-burden
                        </span>
                      </div>
                    </td>

                    <td className="px-3 py-2 text-right">
                      {editMode ? (
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500 text-[10px]">$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={
                                state.unitRate ?? String(item.unitRate || 0)
                              }
                              onChange={(e) => {
                                const newRate = e.target.value;
                                const originalRate = item.unitRate || 0;
                                const parsed = parseFloat(newRate) || 0;
                                const rateChanged =
                                  Math.abs(parsed - originalRate) > 0.001;
                                setLineField(item, idx, "unitRate", newRate);
                                setLineField(
                                  item,
                                  idx,
                                  "rateSource",
                                  rateChanged
                                    ? "manual"
                                    : item.rateSource || "budget",
                                );
                              }}
                              disabled={isRemoved}
                              className="w-24 px-2 py-1 text-xs text-right bg-gray-700 border border-orange-500/60 rounded text-orange-200 font-mono focus:border-orange-500 focus:outline-none disabled:opacity-40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                          <RateSourceBadge source={currentRateSource} />
                        </div>
                      ) : (
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="text-gray-300 font-mono">
                            $
                            {parseFloat(item.unitRate || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                              },
                            )}
                          </span>
                          <RateSourceBadge source={item.rateSource} />
                        </div>
                      )}
                    </td>

                    <td className="px-3 py-2 text-right">
                      {editMode ? (
                        <input
                          type="number"
                          min="0"
                          value={state.qty ?? String(item.quantity || 0)}
                          onChange={(e) =>
                            setLineField(item, idx, "qty", e.target.value)
                          }
                          disabled={isRemoved}
                          className="w-28 px-2 py-1 text-xs text-right bg-gray-700 border border-orange-500/60 rounded text-orange-200 font-mono focus:border-orange-500 focus:outline-none disabled:opacity-40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      ) : (
                        <span className="text-gray-200 font-mono">
                          {(item.quantity || 0).toLocaleString()}{" "}
                          <span className="text-gray-500">{item.uom}</span>
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-2 text-right text-gray-300 font-mono">
                      ${fmt(dispSub)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 font-mono">
                      ${fmt(dispBurden)}
                    </td>
                    <td className="px-3 py-2 text-right text-orange-300 font-mono font-semibold">
                      ${fmt(dispTotal)}
                    </td>

                    {editMode && (
                      <td className="px-2 py-2 text-center">
                        {isRemoved ? (
                          <button
                            title="Restore item"
                            onClick={() =>
                              setRemovedIds((p) => {
                                const n = new Set(p);
                                n.delete(itemKey(item, idx));
                                return n;
                              })
                            }
                            className="text-gray-500 hover:text-gray-300 text-[11px] px-1"
                          >
                            ↩
                          </button>
                        ) : (
                          <button
                            title="Remove item"
                            onClick={() =>
                              setRemovedIds(
                                (p) => new Set([...p, itemKey(item, idx)]),
                              )
                            }
                            className="text-gray-600 hover:text-red-400 transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-3.5 h-3.5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}

              {filteredLineItems.length === 0 && (
                <tr>
                  <td
                    colSpan={editMode ? 12 : 12}
                    className="px-3 py-8 text-center text-gray-500"
                  >
                    No items
                    {bidAreaFilter !== "all"
                      ? ` for area "${bidAreaFilter}"`
                      : ""}
                    .
                  </td>
                </tr>
              )}
            </tbody>

            <tfoot className="bg-gray-900 border-t border-gray-600">
              <tr>
                <td colSpan={8} className="px-3 py-2.5 text-xs text-gray-400">
                  {filteredLineItems.length} item
                  {filteredLineItems.length !== 1 ? "s" : ""}
                  {editMode && removedIds.size > 0 && (
                    <span className="ml-2 text-red-400">
                      ({removedIds.size} marked for removal)
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-200 font-mono">
                  ${fmt(totals.subtotal)}
                </td>
                <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-300 font-mono">
                  ${fmt(totals.burden)}
                </td>
                <td className="px-3 py-2.5 text-right text-sm font-bold text-orange-300 font-mono">
                  ${fmt(totals.total)}
                </td>
                {editMode && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── WO Availability ── */}
      {woAvailRows.length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700 flex items-center gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-white">
              Work Order Availability
            </h3>
            <span className="text-xs text-gray-500">
              — WO-level qty status for items on this log
            </span>
            {woBudgetGroups.length === 0 && (
              <span className="ml-auto text-[10px] text-yellow-600 animate-pulse">
                Loading WO data…
              </span>
            )}
          </div>

          <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-5 gap-3 border-b border-gray-700/50">
            {[
              {
                label: "Estimated",
                value: woTotals.estimated,
                color: "text-blue-300",
              },
              {
                label: "Total Installed To Date",
                value: woTotals.installed,
                color: "text-green-300",
              },
              {
                label: "Pending (others)",
                value: woTotals.pendingOther,
                color: "text-yellow-400",
              },
              {
                label: "This Log",
                value: woTotals.thisLog,
                color: "text-orange-300",
              },
              {
                label: "Remaining",
                value: Math.max(0, woTotals.remaining),
                color:
                  woTotals.remaining <= 0
                    ? "text-red-400"
                    : woTotals.remaining / (woTotals.estimated || 1) < 0.2
                      ? "text-yellow-400"
                      : "text-emerald-400",
                flag: woTotals.remaining <= 0 ? "red" : undefined,
              },
            ].map(({ label, value, color, flag }) => (
              <div
                key={label}
                className={cls(
                  "rounded-lg border p-3",
                  flag === "red"
                    ? "bg-red-950/40 border-red-700/40"
                    : "bg-gray-900/60 border-gray-700",
                )}
              >
                <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                  {label}
                </p>
                <p className={cls("text-lg font-bold font-mono", color)}>
                  {fmtQty(value)}
                </p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-900">
                <tr>
                  {[
                    ["Code", "text-left"],
                    ["Description", "text-left"],
                    ["UOM", "text-left"],
                    ["Estimated", "text-right"],
                    ["Installed", "text-right"],
                    ["Pending (others)", "text-right"],
                    ["This Log", "text-right"],
                    ["Remaining", "text-right"],
                    ["Progress", "text-left min-w-[150px]"],
                  ].map(([h, a]) => (
                    <th
                      key={h}
                      className={cls("px-3 py-2 text-gray-400 font-medium", a)}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {woAvailRows.map((row, idx) => {
                  const remPct =
                    row.estimated > 0
                      ? (row.remaining / row.estimated) * 100
                      : 0;
                  return (
                    <tr
                      key={row.budgetItemId || idx}
                      className={
                        idx % 2 === 0 ? "bg-gray-800/30" : "bg-gray-700/10"
                      }
                    >
                      <td className="px-3 py-2 font-mono text-orange-400">
                        {row.itemCode || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-300 max-w-[200px] truncate">
                        {row.description || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {row.uom || "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-blue-300 font-mono">
                        {fmtQty(row.estimated)}
                      </td>
                      <td className="px-3 py-2 text-right text-green-300 font-mono">
                        {fmtQty(row.installed)}
                      </td>
                      <td className="px-3 py-2 text-right text-yellow-400 font-mono">
                        {row.pendingOther > 0 ? (
                          fmtQty(row.pendingOther)
                        ) : (
                          <span className="text-gray-700">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-orange-300 font-mono font-semibold">
                        {fmtQty(row.thisLog)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">
                        <span
                          className={
                            remPct <= 0
                              ? "text-red-400"
                              : remPct < 20
                                ? "text-yellow-400"
                                : "text-emerald-400"
                          }
                        >
                          {row.remaining > 0 ? (
                            fmtQty(row.remaining)
                          ) : (
                            <span className="text-[10px]">none left</span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <QtyBar
                          installed={row.installed}
                          pending={
                            row.pendingOther +
                            (log.status === "pending" ? row.thisLog : 0)
                          }
                          estimated={row.estimated}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-900 border-t border-gray-600">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-xs text-gray-500">
                    {woAvailRows.length} item
                    {woAvailRows.length !== 1 ? "s" : ""}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-blue-300 font-mono">
                    {fmtQty(woTotals.estimated)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-green-300 font-mono">
                    {fmtQty(woTotals.installed)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-yellow-400 font-mono">
                    {woTotals.pendingOther > 0
                      ? fmtQty(woTotals.pendingOther)
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-orange-300 font-mono">
                    {fmtQty(woTotals.thisLog)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold font-mono">
                    <span
                      className={
                        woTotals.remaining <= 0
                          ? "text-red-400"
                          : "text-emerald-400"
                      }
                    >
                      {fmtQty(Math.max(0, woTotals.remaining))}
                    </span>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="px-5 py-2.5 flex items-center gap-5 text-[10px] text-gray-500 border-t border-gray-700/40 flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" />
              Installed (approved)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-600/60 inline-block" />
              Pending (all logs)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-700 inline-block" />
              Remaining budget qty
            </span>
          </div>
        </div>
      )}

      {/* ── Totals + actions ── */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 px-5 py-4 flex items-center justify-between flex-wrap gap-4">
        <div className="flex flex-wrap gap-6">
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
              Subtotal (base pay)
            </p>
            <p className="font-mono tabular-nums text-gray-200 text-sm">
              ${fmt(totals.subtotal)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
              Burden
            </p>
            <p className="font-mono tabular-nums text-gray-300 text-sm">
              ${fmt(totals.burden)}
            </p>
          </div>
          <div className="border-l border-gray-600 pl-6">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
              Total Cost
            </p>
            <p className="font-mono tabular-nums text-xl font-bold text-orange-400">
              ${fmt(totals.total)}
            </p>
          </div>
          {editMode && (
            <div className="border-l border-gray-600 pl-6">
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                Note
              </p>
              <p className="text-[10px] text-gray-500">
                Burden recalculated server-side on save
              </p>
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {editMode ? (
            <>
              <button
                onClick={handleSave}
                disabled={acting}
                className={cls(
                  "px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors",
                  acting
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-orange-500 hover:bg-orange-600",
                )}
              >
                {acting
                  ? "Saving…"
                  : addedCount > 0
                    ? `Save (${addedCount} new)`
                    : "Save Changes"}
              </button>
              <button
                onClick={exitEditMode}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {isPending && (
                <>
                  <button
                    disabled={acting}
                    onClick={() =>
                      setConfirm({
                        action: "approve",
                        title: "Approve Log",
                        message:
                          "Approve this piecework log? Quantities will be marked as installed.",
                        label: "Approve",
                        color: "green",
                      })
                    }
                    className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40"
                  >
                    ✓ Approve
                  </button>
                  <button
                    disabled={acting}
                    onClick={() =>
                      setConfirm({
                        action: "reject",
                        title: "Reject Log",
                        message:
                          "Reject this piecework log? Submitted quantities will be released.",
                        label: "Reject",
                        color: "red",
                      })
                    }
                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40"
                  >
                    ✕ Reject
                  </button>
                </>
              )}

              {isApproved && (
                <button
                  disabled={acting}
                  onClick={() =>
                    setConfirm({
                      action: "reactivate",
                      title: "Reverse Approval",
                      message:
                        "Move this log back to Pending? The installed quantities will be released.",
                      label: "Reverse Approval",
                      color: "orange",
                    })
                  }
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-40"
                >
                  ↩ Reverse Approval
                </button>
              )}

              {isRejected && (
                <button
                  disabled={acting}
                  onClick={() =>
                    setConfirm({
                      action: "reactivate",
                      title: "Reactivate Log",
                      message: "Reactivate this log back to pending?",
                      label: "Reactivate",
                      color: "orange",
                    })
                  }
                  className="px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-40"
                >
                  ↩ Reactivate
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
